import { useCallback, useEffect, useRef, useState, type FocusEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, Menu, Moon, Sun, X } from "lucide-react";
import { useTheme } from "next-themes";
import { getCtaLabel, localize, siteContent } from "@/content/site";
import { useTranslation } from "@/i18n/useTranslation";
import PrimaryCtaLink from "@/components/PrimaryCtaLink";
import VertLogo from "@/components/VertLogo";

type MenuKey = "services" | "industries";

type NavigationItem = {
  href: string;
  label: string;
  description: string;
};

const isActivePath = (pathname: string, href: string) => (
  href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`)
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuKey | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const dropdownButtonRefs = useRef<Record<MenuKey, HTMLButtonElement | null>>({
    services: null,
    industries: null,
  });
  const closeMenuTimeoutRef = useRef<number | null>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { t, locale, setLocale } = useTranslation();
  const isFr = locale === "fr";

  const serviceLinks = siteContent.services.map((service) => ({
    href: service.href,
    label: localize(service.name, locale),
    description: localize(service.description, locale),
  }));

  const industryLinks = siteContent.industries.map((industry) => ({
    href: industry.href,
    label: localize(industry.label, locale),
    description: isFr
      ? "Explorer les contraintes, données et flux typiques de ce secteur."
      : "Explore the operating constraints, data, and workflows for this sector.",
  }));

  const directLinks = [
    {
      href: "/work",
      label: t("nav.work"),
      description: isFr
        ? "Preuves de livraison, expérience fondateur et standards de production."
        : "Delivery proof, founder experience, and production standards.",
    },
    {
      href: "/insights",
      label: t("nav.insights"),
      description: isFr
        ? "Cadres et analyses sur l’IA en production."
        : "Frameworks and analysis on production AI.",
    },
    {
      href: "/about",
      label: t("nav.about"),
      description: isFr
        ? "La façon dont Vert AI travaille et qui construit."
        : "How Vert AI works and who builds it.",
    },
  ];

  const clearCloseMenuTimeout = useCallback(() => {
    if (closeMenuTimeoutRef.current) {
      window.clearTimeout(closeMenuTimeoutRef.current);
      closeMenuTimeoutRef.current = null;
    }
  }, []);

  const openDesktopMenu = useCallback((menuKey: MenuKey) => {
    clearCloseMenuTimeout();
    setActiveMenu(menuKey);
  }, [clearCloseMenuTimeout]);

  const closeDesktopMenu = useCallback(() => {
    clearCloseMenuTimeout();
    setActiveMenu(null);
  }, [clearCloseMenuTimeout]);

  const scheduleDesktopMenuClose = useCallback((menuKey: MenuKey) => {
    clearCloseMenuTimeout();
    closeMenuTimeoutRef.current = window.setTimeout(() => {
      closeMenuTimeoutRef.current = null;
      setActiveMenu((currentMenu) => (currentMenu === menuKey ? null : currentMenu));
    }, 150);
  }, [clearCloseMenuTimeout]);

  useEffect(() => setMounted(true), []);

  useEffect(() => clearCloseMenuTimeout, [clearCloseMenuTimeout]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    closeDesktopMenu();
    window.scrollTo(0, 0);
  }, [closeDesktopMenu, location.pathname]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        const menuToRestore = activeMenu;
        const shouldRestoreMobileFocus = mobileOpen;
        clearCloseMenuTimeout();
        setMobileOpen(false);
        setActiveMenu(null);

        window.requestAnimationFrame(() => {
          if (menuToRestore) {
            dropdownButtonRefs.current[menuToRestore]?.focus();
          } else if (shouldRestoreMobileFocus) {
            mobileMenuButtonRef.current?.focus();
          }
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeMenu, clearCloseMenuTimeout, mobileOpen]);

  const closeDesktopMenuOnBlur = (event: FocusEvent<HTMLDivElement>) => {
    clearCloseMenuTimeout();
    const nextTarget = event.relatedTarget;
    if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) {
      closeDesktopMenu();
    }
  };

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");
  const toggleLocale = () => setLocale(locale === "en" ? "fr" : "en");
  const primaryCtaLabel = getCtaLabel(siteContent.ctas.primary, locale, "shortLabel");
  const isDarkTheme = !mounted || theme === "dark";
  const topText = scrolled || !isDarkTheme ? "text-foreground" : "text-[hsl(var(--sd-fg))]";
  const topMuted = scrolled || !isDarkTheme ? "text-muted-foreground/78" : "text-[hsl(var(--sd-fg)/0.62)]";
  const topHover = scrolled || !isDarkTheme ? "hover:text-foreground" : "hover:text-[hsl(var(--sd-fg))]";
  const navShell = scrolled
    ? "border-border/45 bg-background/78"
    : isDarkTheme
      ? "border-[hsl(var(--sd-fg)/0.1)] bg-[hsl(var(--control-panel)/0.2)]"
      : "border-border/55 bg-background/82 shadow-[0_14px_34px_hsl(var(--control-ink)/0.07)] backdrop-blur-xl";

  const renderDesktopDropdown = (
    menuKey: MenuKey,
    label: string,
    overview: NavigationItem,
    links: NavigationItem[],
  ) => {
    const open = activeMenu === menuKey;
    const active = links.some((link) => isActivePath(location.pathname, link.href)) || isActivePath(location.pathname, overview.href);

    return (
      <div
        className="relative"
        onMouseEnter={() => openDesktopMenu(menuKey)}
        onMouseLeave={() => scheduleDesktopMenuClose(menuKey)}
        onFocus={() => openDesktopMenu(menuKey)}
        onBlur={closeDesktopMenuOnBlur}
      >
        <button
          ref={(node) => {
            dropdownButtonRefs.current[menuKey] = node;
          }}
          type="button"
          onClick={() => {
            if (open) {
              closeDesktopMenu();
            } else {
              openDesktopMenu(menuKey);
            }
          }}
          onFocus={() => openDesktopMenu(menuKey)}
          aria-expanded={open}
          aria-haspopup="true"
          aria-controls={`${menuKey}-navigation`}
          aria-current={active ? "page" : undefined}
          className={`focus-ring relative inline-flex h-10 items-center gap-1.5 px-3 font-mono text-[10.5px] font-medium uppercase tracking-[0.12em] transition-colors duration-400 ${topHover} ${
            active
              ? scrolled || !isDarkTheme
                ? "bg-foreground/5 text-foreground"
                : "bg-[hsl(var(--sd-fg)/0.06)] text-[hsl(var(--sd-fg))]"
              : topMuted
          }`}
        >
          {label}
          <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
          {active && <span className="absolute -bottom-1 left-0 right-0 h-px bg-primary/40" />}
        </button>

        {open && (
          <>
            <div
              aria-hidden="true"
              className="absolute left-0 top-full z-40 h-3 w-[min(34rem,calc(100vw-2rem))]"
            />
            <div
              id={`${menuKey}-navigation`}
              className="absolute left-0 top-full z-50 mt-3 w-[min(34rem,calc(100vw-2rem))] border border-border/35 bg-background/95 p-2 shadow-[0_24px_70px_hsl(var(--control-ink)/0.18)] backdrop-blur-xl"
            >
              <Link
                to={overview.href}
                onClick={closeDesktopMenu}
                aria-current={isActivePath(location.pathname, overview.href) ? "page" : undefined}
                className="focus-ring group block border-b border-border/25 p-4 transition-colors duration-300 hover:bg-surface-1"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary/70">
                  {isFr ? "Vue d’ensemble" : "Overview"}
                </span>
                <span className="mt-2 block text-[14px] font-semibold leading-snug text-foreground group-hover:text-primary">
                  {overview.label}
                </span>
                <span className="mt-1.5 block max-w-md text-[12.5px] leading-[1.65] text-muted-foreground">
                  {overview.description}
                </span>
              </Link>
              <ul className="grid grid-cols-1 gap-1 pt-2 sm:grid-cols-2">
                {links.map((link) => (
                  <li key={`${menuKey}-${link.href}-${link.label}`}>
                    <Link
                      to={link.href}
                      onClick={closeDesktopMenu}
                      aria-current={isActivePath(location.pathname, link.href) ? "page" : undefined}
                      className="focus-ring group block min-h-[5.5rem] p-4 transition-colors duration-300 hover:bg-surface-1"
                    >
                      <span className="block text-[13px] font-semibold leading-snug text-foreground/90 group-hover:text-primary">
                        {link.label}
                      </span>
                      <span className="mt-1.5 block text-[12px] leading-[1.6] text-muted-foreground/75">
                        {link.description}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderMobileGroup = (
    label: string,
    overview: NavigationItem,
    links: NavigationItem[],
  ) => (
    <details className="group border-b border-border/20 py-2.5">
      <summary
        aria-label={isFr ? `Afficher ${label}` : `Show ${label}`}
        className="focus-ring flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 px-3 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-foreground transition-colors hover:bg-surface-1 [&::-webkit-details-marker]:hidden"
      >
        <span>{label}</span>
        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-300 group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="pb-3 pt-2">
        <Link
          to={overview.href}
          onClick={() => setMobileOpen(false)}
          aria-current={isActivePath(location.pathname, overview.href) ? "page" : undefined}
          className="focus-ring block border-l border-primary/45 bg-primary/[0.035] py-3 pl-4 pr-3 text-[14px] font-semibold text-foreground"
        >
          {overview.label}
          <span className="mt-1 block text-[12.5px] font-normal leading-[1.55] text-muted-foreground">
            {overview.description}
          </span>
        </Link>
        <ul className="mt-3 space-y-1">
          {links.map((link) => (
            <li key={`${label}-${link.href}-${link.label}`}>
              <Link
                to={link.href}
                onClick={() => setMobileOpen(false)}
                aria-current={isActivePath(location.pathname, link.href) ? "page" : undefined}
                className="focus-ring flex min-h-11 items-center px-4 py-2 text-[13.5px] leading-snug text-muted-foreground transition-colors duration-300 hover:bg-surface-1 hover:text-foreground"
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );

  return (
    <header
      className={`fixed left-0 right-0 top-0 z-50 transition-all duration-700 ease-out ${
        scrolled
          ? "glass border-b border-border/30 shadow-[0_1px_0_hsl(var(--foreground)/0.04),0_14px_42px_hsl(var(--control-ink)/0.06)]"
          : isDarkTheme
            ? "border-b border-transparent bg-transparent"
            : "border-b border-border/30 bg-background/88 shadow-[0_1px_0_hsl(var(--foreground)/0.04),0_14px_36px_hsl(var(--control-ink)/0.06)] backdrop-blur-xl"
      }`}
    >
      <nav className="container-wide flex h-14 items-center justify-between lg:h-16" aria-label={t("nav.primaryAria")}>
        <Link
          to="/"
          className="focus-ring group flex items-center"
          aria-label={isFr ? "Accueil Vert AI" : "Vert AI home"}
          aria-current={location.pathname === "/" ? "page" : undefined}
        >
          <VertLogo className={`h-5 w-auto transition-opacity duration-300 group-hover:opacity-70 lg:h-[22px] ${topText}`} />
          <span className="sr-only">Vert AI</span>
        </Link>

        <div className={`hidden items-center gap-1 border px-2 py-1 transition-colors duration-500 lg:flex ${navShell}`}>
          {renderDesktopDropdown(
            "services",
            t("nav.services"),
            {
              href: "/services",
              label: t("nav.servicesOverview"),
              description: isFr
                ? "Comparer les mandats IA, données et décision selon le problème à regler."
                : "Compare AI, data, and decision-system engagements by operating problem.",
            },
            serviceLinks,
          )}
          {renderDesktopDropdown(
            "industries",
            t("nav.industries"),
            {
              href: "/industries",
              label: t("nav.industriesOverview"),
              description: isFr
                ? "Voir où Vert AI concentre ses systèmes IA, données et décision."
                : "See where Vert AI focuses AI, data, and decision systems.",
            },
            industryLinks,
          )}

          {directLinks.map((link) => {
            const active = isActivePath(location.pathname, link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                aria-current={active ? "page" : undefined}
                className={`focus-ring relative inline-flex h-10 items-center px-3 font-mono text-[10.5px] font-medium uppercase tracking-[0.12em] transition-colors duration-400 ${topHover} ${
                  active
                    ? scrolled || !isDarkTheme
                      ? "bg-foreground/5 text-foreground"
                      : "bg-[hsl(var(--sd-fg)/0.06)] text-[hsl(var(--sd-fg))]"
                    : topMuted
                }`}
              >
                {link.label}
                {active && <span className="absolute -bottom-1 left-0 right-0 h-px bg-primary/40" />}
              </Link>
            );
          })}

          {mounted && (
            <>
              <button
                onClick={toggleLocale}
                className={`focus-ring flex h-10 w-10 items-center justify-center border-l font-mono text-[10px] font-medium tracking-[0.12em] transition-colors duration-400 ${scrolled || !isDarkTheme ? "border-border/45" : "border-[hsl(var(--sd-fg)/0.1)]"} ${topMuted} ${topHover}`}
                aria-label={locale === "en" ? "Switch to French" : "Switch to English"}
              >
                {locale === "en" ? "FR" : "EN"}
              </button>
              <button
                onClick={toggleTheme}
                className={`focus-ring flex h-10 w-10 items-center justify-center transition-colors duration-400 ${topMuted} ${topHover}`}
                aria-label="Toggle dark mode"
              >
                {theme === "dark" ? <Sun className="h-3.5 w-3.5" aria-hidden="true" /> : <Moon className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            </>
          )}

          <PrimaryCtaLink
            labelVariant="shortLabel"
            dataEventLocation="header_desktop"
            dataEventSurface="primary_navigation"
            className={`focus-ring ml-1 inline-flex h-10 items-center justify-center gap-2 px-5 font-mono text-[10px] font-medium uppercase tracking-[0.08em] transition-all duration-600 ${
              scrolled || !isDarkTheme
                ? "border border-foreground bg-foreground text-background hover:bg-transparent hover:text-foreground"
                : "border border-[hsl(var(--sd-fg)/0.18)] bg-transparent text-[hsl(var(--sd-fg))] hover:border-primary/55 hover:text-primary"
            }`}
          >
            {primaryCtaLabel}
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </PrimaryCtaLink>
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          {mounted && (
            <>
              <button
                onClick={toggleLocale}
                className={`focus-ring flex h-11 w-11 items-center justify-center font-mono text-[10px] font-medium tracking-wider transition-colors ${topMuted} ${topHover}`}
                aria-label={locale === "en" ? "Switch to French" : "Switch to English"}
              >
                {locale === "en" ? "FR" : "EN"}
              </button>
              <button
                onClick={toggleTheme}
                className={`focus-ring flex h-11 w-11 items-center justify-center transition-colors ${topMuted} ${topHover}`}
                aria-label="Toggle dark mode"
              >
                {theme === "dark" ? <Sun className="h-3.5 w-3.5" aria-hidden="true" /> : <Moon className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            </>
          )}
          <button
            ref={mobileMenuButtonRef}
            className={`focus-ring -mr-2 flex h-11 w-11 items-center justify-center ${topText}`}
            onClick={() => setMobileOpen((value) => !value)}
            aria-label={mobileOpen ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-controls="mobile-navigation"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div
          className="isolate min-h-[calc(100vh-3.5rem)] border-t border-border/45 bg-background/96 shadow-[0_24px_60px_hsl(var(--control-ink)/0.16)] backdrop-blur-xl dark:border-[hsl(var(--sd-fg)/0.1)] dark:bg-[hsl(var(--control-ink))] lg:hidden"
          id="mobile-navigation"
        >
          <nav className="container-wide max-h-[calc(100vh-3.5rem)] overflow-y-auto py-5" aria-label={t("nav.mobileAria")}>
            <div className="mb-5 border-b border-border/20 pb-5">
              <PrimaryCtaLink
                labelVariant="shortLabel"
                onClick={() => setMobileOpen(false)}
                dataEventLocation="header_mobile"
                dataEventSurface="mobile_navigation"
                className="focus-ring btn-primary w-full justify-center"
              >
                {primaryCtaLabel}
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </PrimaryCtaLink>
            </div>
            {renderMobileGroup(
              t("nav.services"),
              {
                href: "/services",
                label: t("nav.servicesOverview"),
                description: isFr ? "Choisir le bon mandat." : "Choose the right engagement.",
              },
              serviceLinks,
            )}
            {renderMobileGroup(
              t("nav.industries"),
              {
                href: "/industries",
                label: t("nav.industriesOverview"),
                description: isFr ? "Explorer les secteurs prioritaires." : "Explore priority sectors.",
              },
              industryLinks,
            )}
            <div className="py-3">
              {directLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  aria-current={isActivePath(location.pathname, link.href) ? "page" : undefined}
                  className={`focus-ring block min-h-[4.25rem] border-b border-border/15 py-4 transition-colors hover:text-foreground ${
                    isActivePath(location.pathname, link.href)
                      ? "text-foreground"
                      : "text-muted-foreground/92 dark:text-[hsl(var(--sd-fg)/0.68)]"
                  }`}
                >
                  <span className="block font-mono text-[11px] font-medium uppercase tracking-[0.12em]">{link.label}</span>
                  <span className="mt-1 block text-[12.5px] leading-[1.55]">{link.description}</span>
                </Link>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
